import { Preferences } from '@capacitor/preferences';

export type DBNamespace = string;

export interface DBRecord {
  id: string;
  createdAt: number;
  lastModified: number;
  [key: string]: any;
}

function getStorageKey(namespace: DBNamespace, id: string) {
  return `${namespace}:${id}`;
}

export const PreferencesDB = {
  async save<T extends DBRecord>(type: { new (): T }, record: DBRecord): Promise<void> {
    const key = getStorageKey(type.name, record.id);
    await Preferences.set({
      key,
      value: JSON.stringify(record),
    });
  },

  async get(namespace: DBNamespace, id: string): Promise<DBRecord | null> {
    const key = getStorageKey(namespace, id);
    const { value } = await Preferences.get({ key });
    return value ? JSON.parse(value) : null;
  },

  async delete(namespace: DBNamespace, id: string): Promise<void> {
    const key = getStorageKey(namespace, id);
    await Preferences.remove({ key });
  },

  async getAll<T extends DBRecord>(type: { new (): T }): Promise<DBRecord[]> {
    const { keys } = await Preferences.keys();
    const matchingKeys = keys.filter((k) => k.startsWith(`${type.name}:`));
    const records: DBRecord[] = [];
    for (const key of matchingKeys) {
      const { value } = await Preferences.get({ key });
      if (value) {
        const deserialized = JSON.parse(value);
        const emptyObject = new type();
        Object.assign(emptyObject, deserialized);
        records.push(emptyObject);
      }
    }
    return records;
  },

  async clearNamespace<T extends DBRecord>(type: { new (): T }): Promise<void> {
    const { keys } = await Preferences.keys();
    const matchingKeys = keys.filter((k) => k.startsWith(`${type.name}:`));
    for (const key of matchingKeys) {
      await Preferences.remove({ key });
    }
  },
};
